import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { CheckCircle } from 'lucide-react';

// Step status type
export type StepStatus = 'pending' | 'current' | 'completed';

// Individual step interface
export interface Step {
  id: string;
  label: string;
  description?: string;
  status: StepStatus;
  disabled?: boolean;
  icon?: React.ReactNode;
}

// Steps component variants using CVA
const stepsVariants = cva('w-full', {
  variants: {
    orientation: {
      horizontal: 'flex items-center',
      vertical: 'flex flex-col space-y-4',
    },
    size: {
      sm: '',
      md: '',
      lg: '',
    },
    variant: {
      default: '',
      minimal: '',
      numbered: '',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    size: 'md',
    variant: 'default',
  },
});

const stepIndicatorVariants = cva(
  'relative flex items-center justify-center font-medium transition-all duration-200 ease-in-out select-none',
  {
    variants: {
      status: {
        pending: 'bg-gray-200 text-gray-600 border-2 border-gray-200',
        current: 'bg-primary-600 text-white border-2 border-primary-600 shadow-sm ring-4 ring-primary-100',
        completed: 'bg-success-600 text-white border-2 border-success-600',
      },
      size: {
        sm: 'w-6 h-6 text-xs rounded-full',
        md: 'w-8 h-8 text-sm rounded-full',
        lg: 'w-10 h-10 text-base rounded-full',
      },
      variant: {
        default: '',
        minimal: 'border-0',
        numbered: '',
      },
    },
    defaultVariants: {
      status: 'pending',
      size: 'md',
      variant: 'default',
    },
  }
);

const stepConnectorVariants = cva('flex-1 transition-colors duration-200', {
  variants: {
    status: {
      pending: 'bg-gray-200',
      current: 'bg-gray-200',
      completed: 'bg-success-600',
    },
    orientation: {
      horizontal: 'h-0.5 mx-3',
      vertical: 'w-0.5 ml-4 my-2 min-h-[2rem]',
    },
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    status: 'pending',
    orientation: 'horizontal',
    size: 'md',
  },
});

export interface StepsProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stepsVariants> {
  steps: Step[];
  responsive?: boolean;
  showLabels?: boolean;
  showDescriptions?: boolean;
  onStepClick?: (step: Step, index: number) => void;
}

export const Steps = React.forwardRef<HTMLDivElement, StepsProps>(
  (
    {
      steps,
      orientation = 'horizontal',
      size = 'md',
      variant = 'default',
      responsive = true,
      showLabels = true,
      showDescriptions = false,
      onStepClick,
      className,
      ...props
    },
    ref
  ) => {
    const isClickable = Boolean(onStepClick);

    // Mobile-responsive logic
    const isMobile = responsive;
    const mobileOrientation = isMobile ? 'horizontal' : orientation;

    // Find current step for mobile display
    const currentStepIndex = steps.findIndex(step => step.status === 'current');
    const currentStep = steps[currentStepIndex];

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
        role="progressbar"
        aria-label="Progress steps"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={currentStepIndex + 1}
        {...props}
      >
        {/* Desktop: Full steps display */}
        <div className={cn(
          stepsVariants({ orientation }),
          responsive && "hidden sm:flex",
          !responsive && "flex"
        )}>
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            const isCompleted = step.status === 'completed';
            const isCurrent = step.status === 'current';

            return (
              <React.Fragment key={step.id}>
                <div
                  className={cn(
                    'flex flex-col items-center',
                    orientation === 'vertical' && 'flex-row items-start',
                    orientation === 'horizontal' && 'text-center'
                  )}
                  role="step"
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {/* Step Indicator */}
                  <button
                    type="button"
                    disabled={step.disabled || !isClickable}
                    onClick={() => !step.disabled && onStepClick?.(step, index)}
                    className={cn(
                      stepIndicatorVariants({ status: step.status, size, variant }),
                      isClickable && !step.disabled && 'hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                      step.disabled && 'opacity-50 cursor-not-allowed',
                      !isClickable && 'cursor-default'
                    )}
                    aria-label={`Step ${index + 1}: ${step.label}`}
                  >
                    {step.icon ? (
                      step.icon
                    ) : isCompleted ? (
                      <CheckCircle className={cn(
                        size === 'sm' && 'w-3 h-3',
                        size === 'md' && 'w-4 h-4',
                        size === 'lg' && 'w-5 h-5'
                      )} />
                    ) : (
                      index + 1
                    )}
                  </button>

                  {/* Step Content */}
                  {(showLabels || showDescriptions) && (
                    <div className={cn(
                      'mt-2',
                      orientation === 'vertical' && 'ml-4 mt-0 flex-1'
                    )}>
                      {showLabels && (
                        <div
                          className={cn(
                            'font-medium transition-colors duration-200',
                            isCurrent && 'text-primary-700',
                            isCompleted && 'text-success-700',
                            step.status === 'pending' && 'text-gray-600',
                            size === 'sm' && 'text-xs',
                            size === 'md' && 'text-sm',
                            size === 'lg' && 'text-base'
                          )}
                        >
                          {step.label}
                        </div>
                      )}
                      {showDescriptions && step.description && (
                        <div
                          className={cn(
                            'text-gray-500 mt-1',
                            size === 'sm' && 'text-xs',
                            size === 'md' && 'text-xs',
                            size === 'lg' && 'text-sm'
                          )}
                        >
                          {step.description}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Connector Line */}
                {!isLast && (
                  <div
                    className={stepConnectorVariants({
                      status: isCompleted ? 'completed' : 'pending',
                      orientation,
                      size,
                    })}
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Mobile: Compact display */}
        {responsive && (
          <div className="flex sm:hidden items-center justify-center w-full">
            <div className="flex items-center space-x-3">
              {/* Current step - large and prominent */}
              <div className={cn(
                stepIndicatorVariants({ status: 'current', size: 'lg', variant }),
                'shadow-md'
              )}>
                {currentStep?.icon ? (
                  currentStep.icon
                ) : currentStep?.status === 'completed' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  currentStepIndex + 1
                )}
              </div>

              {/* Progress info */}
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-500 font-medium">of</span>
                
                {/* Step dots */}
                <div className="flex items-center space-x-1" aria-hidden="true">
                  {steps.map((step, index) => {
                    if (step.status === 'current') return null;
                    
                    return (
                      <div
                        key={step.id}
                        className={cn(
                          'w-2 h-2 rounded-full transition-colors duration-200',
                          step.status === 'completed' ? 'bg-success-500' : 'bg-gray-300'
                        )}
                      />
                    );
                  })}
                </div>

                <span className="text-gray-600 font-medium">{steps.length}</span>
              </div>

              {/* Current step label */}
              {showLabels && currentStep && (
                <div className="text-sm font-medium text-primary-700 ml-2">
                  {currentStep.label}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

Steps.displayName = 'Steps';

// Helper function to create steps from simple data
export function createSteps(
  stepLabels: string[],
  currentIndex: number = 0
): Step[] {
  return stepLabels.map((label, index) => ({
    id: `step-${index}`,
    label,
    status: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'pending',
  }));
}

// Helper function to update step status
export function updateStepStatus(
  steps: Step[],
  newCurrentIndex: number
): Step[] {
  return steps.map((step, index) => ({
    ...step,
    status: index < newCurrentIndex 
      ? 'completed' 
      : index === newCurrentIndex 
        ? 'current' 
        : 'pending',
  }));
}